import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { inrCompact, statusLabel, STATUS_COLORS, PIPELINE, LOAN_TYPES } from "../../lib/site";

export default function Leads() {
  const [leads, setLeads] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", email: "", loan_type: "", amount: "", city: "" });

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    api.get(`/admin/leads?${params}`).then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, [status, q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const createLead = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/leads", { ...form, amount: Number(form.amount) || 0, consent: true });
      toast.success("Lead created");
      setShowNew(false);
      setForm({ name: "", mobile: "", email: "", loan_type: "", amount: "", city: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const scoreBadge = (l) => l.score_label === "hot" ? "bg-red-50 text-red-700 border-red-200" : l.score_label === "warm" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-gray-100 text-gray-500 border-gray-200";

  return (
    <div data-testid="admin-leads-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-brand-dark">Leads</h1>
          <p className="text-sm text-brand-muted mt-0.5">Every website enquiry lands here</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="new-lead-btn" className="btn-primary !px-5 !py-2.5 !text-xs"><Plus size={14} /> New Lead</button>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input data-testid="leads-search" className="input-site !pl-10" placeholder="Search name, mobile, lead ID, city…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select data-testid="leads-status-filter" className="input-site !w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
          <option value="">All Stages</option>
          {PIPELINE.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
      </div>

      {showNew && (
        <form onSubmit={createLead} className="bg-white rounded-xl border border-black/5 p-5 mb-5 grid sm:grid-cols-3 gap-3" data-testid="new-lead-form">
          <input required className="input-site" placeholder="Full Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-lead-name" />
          <input required className="input-site" placeholder="Mobile *" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="new-lead-mobile" />
          <input className="input-site" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <select required className="input-site" value={form.loan_type} onChange={(e) => setForm({ ...form, loan_type: e.target.value })}>
            <option value="" disabled>Loan Type *</option>
            {LOAN_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
          <input type="number" className="input-site" placeholder="Amount (₹)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <input className="input-site" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <div className="sm:col-span-3 flex gap-2">
            <button type="submit" data-testid="new-lead-submit" className="btn-primary !px-5 !py-2.5 !text-xs">Create Lead</button>
            <button type="button" onClick={() => setShowNew(false)} className="btn-outline !px-5 !py-2.5 !text-xs"><X size={14} /> Cancel</button>
          </div>
        </form>
      )}

      {!leads ? <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-brand-red" size={28} /></div> : (
        <div className="bg-white rounded-xl border border-black/5 overflow-x-auto" data-testid="leads-table">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-brand-muted border-b border-black/5">
                <th className="px-5 py-3 font-bold">Lead</th><th className="px-5 py-3 font-bold">Contact</th>
                <th className="px-5 py-3 font-bold">Product</th><th className="px-5 py-3 font-bold">Amount</th>
                <th className="px-5 py-3 font-bold">Score</th><th className="px-5 py-3 font-bold">Status</th>
                <th className="px-5 py-3 font-bold">Assigned</th><th className="px-5 py-3 font-bold">Created</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-black/5 last:border-0 hover:bg-black/[0.015]">
                  <td className="px-5 py-3">
                    <Link to={`/admin/leads/${l.id}`} data-testid={`lead-row-${l.lead_id}`} className="font-semibold text-brand-dark hover:text-brand-red">{l.name}</Link>
                    <p className="text-xs text-brand-muted">{l.lead_id}</p>
                  </td>
                  <td className="px-5 py-3 text-xs text-brand-dark/80">{l.mobile}<p className="text-brand-muted">{l.city || ""}</p></td>
                  <td className="px-5 py-3 text-brand-dark/80 text-xs">{l.loan_type}</td>
                  <td className="px-5 py-3 font-semibold">{inrCompact(l.amount)}</td>
                  <td className="px-5 py-3"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase ${scoreBadge(l)}`}>{l.score_label}</span></td>
                  <td className="px-5 py-3"><span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${STATUS_COLORS[l.status] || ""}`}>{statusLabel(l.status)}</span></td>
                  <td className="px-5 py-3 text-xs text-brand-muted">{l.assigned_name || "Unassigned"}</td>
                  <td className="px-5 py-3 text-xs text-brand-muted">{new Date(l.created_at).toLocaleDateString("en-IN")}</td>
                </tr>
              ))}
              {leads.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-brand-muted text-sm">No leads match your filters.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
